import React, { useEffect, useState } from 'react';

// types
import type {
  WThemeGroupSearchResultsView,
  WThemeSearchResultsView,
} from '@zola/svc-web-api-ts-client';

// redux
import { getUserWeddingDate } from 'selectors/user/userSelectors';
import { toastsActions } from '@zola-helpers/client/dist/es/redux/toasts';
import { useAppSelector } from 'reducers/useAppSelector';
import { useAppDispatch } from 'reducers/useAppDispatch';
import {
  fetchWeddingAccount,
  disableWebsiteOnboarding,
  updateWeddingWebsiteThemeV2,
} from 'actions/WeddingActions';

// utils
import { trackOnboardingCompleted } from '@zola-helpers/client/dist/es/tracking/onboard/onboardEvents';
import { SEGMENTS } from 'pages/WebsiteDesignsPLPV2/utils';
import { setCookie } from '@zola-helpers/client/dist/es/util/storage';
import onboardTrackingConstants from '@zola-helpers/client/dist/es/tracking/onboard/onboardTrackingConstants';
import { isDisneyTheme } from 'pages/WebsiteDesignsPLPSSR/helpers';

// components
import { ButtonV3 } from '@zola/zola-ui/src/components/ButtonV3';
import PLPThemeSwatches from 'pages/WebsiteDesignsPLPV2/components/PLPThemeGroup/components/PLPThemeSwatches';

// styles
import { StyledMagicTag } from 'pages/WebsiteDesignsPLPSSR/ThemeGroup/ThemeGroup.styles';
import {
  BrowserBar,
  ThemeGroupActions,
  ThemeGroupActionsAndPrice,
  ThemeGroupContainer,
  ThemeGroupPreviewContainer,
  ThemeGroupPrice,
  ThemeGroupTitle,
  ThemePreviewLink,
} from './PLPThemeGroup.styles';

export type PLPThemeGroupProps = {
  layoutType: string | SEGMENTS;
  themeGroup: WThemeGroupSearchResultsView;
  defaultThemeKey?: string;
  className?: string;
  isPostAuthNav3?: boolean;
};

const PLPThemeGroup: React.FC<PLPThemeGroupProps> = ({
  layoutType,
  themeGroup,
  defaultThemeKey,
  className,
  isPostAuthNav3,
}) => {
  const dispatch = useAppDispatch();

  const userContext = useAppSelector(state => state.user.userContext);
  const weddingTheme = useAppSelector(state => state.wedding?.theme);
  const weddingDate = useAppSelector(getUserWeddingDate);
  const hasSelectedWebsiteTheme = useAppSelector(
    state => state.user.userContext.wedding_account?.website_onboarding_completed
  );

  const [locallySelectedTheme, setLocallySelectedTheme] = useState<WThemeSearchResultsView>(
    themeGroup.themes.find(theme => theme.key === defaultThemeKey) || themeGroup.themes?.[0]
  );
  const [active, setActive] = useState(false);

  const { BUSINESS_UNIT, COMPONENT } = onboardTrackingConstants;
  const pdpLink = userContext.has_wedding_account
    ? `/wedding/manage/website/design/${locallySelectedTheme?.key}?layout=${layoutType}`
    : `/wedding-planning/website/design/${locallySelectedTheme?.key}?layout=${layoutType}`;
  const previewImg = locallySelectedTheme?.assets?.filter(asset => {
    if (`${asset.type}` === 'PLP' && `${asset.layout_type}` === `${layoutType}`) {
      return asset;
    }
    return null;
  })[0]?.image_url;

  const changeThemeView = (e: React.MouseEvent, newTheme: WThemeSearchResultsView) => {
    setLocallySelectedTheme(newTheme);
    setActive(!active);
  };

  const onClickSelect = async () => {
    if (!locallySelectedTheme) return;

    if (userContext.has_completed_onboarding) {
      // postauth flow
      await dispatch(updateWeddingWebsiteThemeV2(locallySelectedTheme?.key, layoutType)).catch(
        () => {
          dispatch(toastsActions.negative({ headline: 'Error updating your theme.' }));
        }
      );
      if (!hasSelectedWebsiteTheme) {
        await dispatch(fetchWeddingAccount())
          .then(() => {
            dispatch(disableWebsiteOnboarding());
            if (typeof window !== 'undefined') {
              trackOnboardingCompleted(BUSINESS_UNIT.WEDDINGS, COMPONENT.WEDDING_WEBSITE, {
                isExistingUser: true,
                weddingDate,
              });
              window.location.assign('/wedding/manage/website/pages');
            }
          })
          .catch(() => {
            dispatch(toastsActions.negative({ headline: 'Error updating your theme.' }));
          });
      }
    } else {
      // preauth flow
      setCookie('preauthSelectedWeddingTheme', themeGroup.key, 1800);
      setCookie('preauthSelectedWeddingThemeKey', locallySelectedTheme?.key, 1800);
      setCookie('preauthSelectedLayout', layoutType, 1800);

      if (typeof window !== 'undefined') {
        window.location.assign('/wedding/onboard/wedding-website/form');
      }
    }
  };

  const onClickDetails = () => {
    if (typeof window !== 'undefined') {
      window.location.assign(pdpLink);
    }
  };

  useEffect(() => {
    if (weddingTheme?.theme_group_key && themeGroup.themes?.length) {
      const matchedTheme = themeGroup.themes.find(theme => theme.key === weddingTheme.theme_key);
      if (matchedTheme) setLocallySelectedTheme(matchedTheme);
    }
  }, [themeGroup, weddingTheme]);

  return (
    <ThemeGroupContainer
      className={className}
      data-testid="PLPThemeGroup"
      data-plpitem
      isPostAuthNav3={isPostAuthNav3}
    >
      <ThemeGroupPreviewContainer isSinglePage={layoutType === SEGMENTS.singlepage}>
        <BrowserBar />
        <ThemePreviewLink
          href={pdpLink}
          style={{ backgroundImage: `url(${previewImg}?w=640&fm=webp)` }}
          onClick={onClickDetails}
          aria-label={themeGroup.title}
        />
      </ThemeGroupPreviewContainer>
      <ThemeGroupTitle href={pdpLink} onClick={onClickDetails}>
        {themeGroup.title}
      </ThemeGroupTitle>
      {isDisneyTheme(themeGroup.themes?.[0]) && <StyledMagicTag />}
      <PLPThemeSwatches
        themeOptions={themeGroup}
        onSwatchClick={changeThemeView}
        currentTheme={locallySelectedTheme}
      />
      <ThemeGroupActionsAndPrice>
        <ThemeGroupPrice>Free</ThemeGroupPrice>
        <ThemeGroupActions>
          <ButtonV3 variant="primary" onClick={onClickSelect}>
            Select
          </ButtonV3>
          <ButtonV3 variant="secondary" onClick={onClickDetails}>
            View Details
          </ButtonV3>
        </ThemeGroupActions>
      </ThemeGroupActionsAndPrice>
    </ThemeGroupContainer>
  );
};

export default PLPThemeGroup;
